import React from 'react';
import { Plus, Trash2 } from 'lucide-react';

// Robustes Parsen: akzeptiert bereits-Array, JSON-String oder Leer.
const parseArr = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string' && v.trim()) {
    try {
      const p = JSON.parse(v);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
};

/**
 * JsonTableEditor — generischer Tabellen-Editor fuer JSON-Array-Felder (F7).
 * Ersetzt die rohen JSON-Textareas im ProductForm.
 *
 * - value:    JSON-String eines Arrays (Speicherformat bleibt unveraendert).
 * - onChange: (jsonString) => void  (serialisiert das Array wieder als String).
 * - columns:  [{ key, label, type?: 'text'|'number', placeholder? }]
 * - Unbekannte Keys pro Zeile bleiben erhalten (kein Datenverlust beim Editieren).
 * - Alle Buttons sind type="button" (loesen das umgebende Formular NICHT aus).
 */
const JsonTableEditor = ({ value, onChange, columns, isDarkMode, t, addLabel }) => {
  const rows = parseArr(value);
  const emit = (next) => onChange(JSON.stringify(next));

  const updateCell = (i, key, type, raw) => {
    const val = type === 'number' ? (raw === '' ? '' : Number(raw)) : raw;
    emit(rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  };
  const addRow = () =>
    emit([...rows, columns.reduce((o, c) => ({ ...o, [c.key]: c.type === 'number' ? 0 : '' }), {})]);
  const removeRow = (i) => emit(rows.filter((_, idx) => idx !== i));

  const cellInput = `w-full px-2 py-1.5 text-sm rounded-lg border focus:outline-none focus:ring-2 ${
    isDarkMode
      ? 'bg-gray-800/40 border-white/20 text-white placeholder-white/30 focus:ring-blue-400/50'
      : 'bg-white border-slate-200 text-slate-800 placeholder-slate-400 focus:ring-brand/40'
  }`;
  const thCls = `text-left text-xs font-medium px-2 pb-1 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`;

  return (
    <div className={`rounded-xl border p-3 ${isDarkMode ? 'border-white/10 bg-gray-900/20' : 'border-slate-200 bg-slate-50/60'}`}>
      {rows.length > 0 ? (
        <table className="w-full border-separate" style={{ borderSpacing: '0 4px' }}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={thCls}>{c.label}</th>
              ))}
              <th className={thCls} style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {columns.map((c) => (
                  <td key={c.key} className="px-1 align-top">
                    <input
                      type={c.type === 'number' ? 'number' : 'text'}
                      step={c.type === 'number' ? 'any' : undefined}
                      value={row[c.key] ?? ''}
                      placeholder={c.placeholder || ''}
                      onChange={(e) => updateCell(i, c.key, c.type, e.target.value)}
                      className={cellInput}
                    />
                  </td>
                ))}
                <td className="px-1 text-center align-middle">
                  <button
                    type="button"
                    onClick={() => removeRow(i)}
                    title={t('admin.jsonEditor.removeRow')}
                    className={`p-1.5 rounded-lg transition-colors ${
                      isDarkMode ? 'text-red-300 hover:bg-red-500/15' : 'text-red-500 hover:bg-red-50'
                    }`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className={`text-xs italic px-1 py-2 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
          {t('admin.jsonEditor.empty')}
        </p>
      )}
      <button
        type="button"
        onClick={addRow}
        className={`mt-2 inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-dashed transition-colors ${
          isDarkMode
            ? 'border-white/20 text-white/70 hover:border-brand/40 hover:text-brand'
            : 'border-slate-300 text-slate-500 hover:border-brand/40 hover:text-brand'
        }`}
      >
        <Plus className="w-4 h-4" /> {addLabel || t('admin.jsonEditor.addRow')}
      </button>
    </div>
  );
};

export default JsonTableEditor;
